import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { MealPlanResponse, Meal } from '@meal-planner/shared';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { useProfile, useTrialDaysLeft } from '../hooks/useAuth.js';
import { PanicModal } from '../components/PanicModal.js';
import { RecipeModal } from '../components/RecipeModal.js';
import { Confetti } from '../components/Confetti.js';
import { GeneratePlanFlow } from '../components/GeneratePlanFlow.js';
import { Icon, type IconName } from '../components/ui/Icon.js';
import { Placeholder, type PlaceholderTone } from '../components/ui/Placeholder.js';
import { WasteSavingsWidget } from '../components/WasteSavingsWidget.js';

interface UserStats {
  totalXp: number;
  level: number;
  levelTitle: string;
  nextLevelXp: number | null;
  weekStreak: number;
  totalPlans: number;
}

interface WeeklyChallenge {
  challenge: { key: string; title: string; description: string; xpReward: number } | null;
  progress: { completedAt: string | null } | null;
}

const LEVEL_XP = [0, 150, 400, 900, 1800, 3500, 6000];
const RESTAURANT_PPP = 16;
const HOME_PPP = 4;

const MEAL_TONE: Record<string, PlaceholderTone> = {
  breakfast: 'warm',
  lunch: 'leaf',
  dinner: 'spice',
};

const FAV_ACCENT: Record<string, string> = {
  breakfast: 'accent-saffron',
  lunch: 'accent-cobalt',
  dinner: 'accent-plum',
};

interface MealPlanSummary { id: string; weekStart: string; createdAt: string; }
interface MealPlanRow { id: string; weekStart: string; planData: MealPlanResponse; createdAt: string; }
interface DenyListItem { id: string; item: string; }
interface FavoriteRecipe { id: string; recipeId: string; recipeName: string; mealType: string; createdAt: string; }

function getNextMonday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}

function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getISOWeek(d: Date): string {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((+date - +yearStart) / 86400000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function formatWeekRange(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00');
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
}

function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function StatCard({ icon, label, value, sub, link, linkTo, highlight }: {
  icon: IconName; label: string; value: ReactNode; sub: string; link: string; linkTo: string; highlight?: boolean;
}) {
  return (
    <div className="card flex flex-col gap-3 p-6"
      style={highlight ? { background: 'var(--accent-soft)', borderColor: 'transparent' } : undefined}>
      <div className="flex items-center gap-2.5"
        style={{ color: highlight ? 'var(--accent)' : 'var(--ink-3)' }}>
        <Icon name={icon} size={16} />
        <span className="text-xs font-semibold tracking-wider uppercase">{label}</span>
      </div>
      <div className="h-display" style={{ fontSize: 44, lineHeight: 1, color: highlight ? 'var(--accent)' : 'var(--ink)' }}>
        {value}
      </div>
      <div className="text-sm" style={{ color: 'var(--ink-3)' }}>{sub}</div>
      <Link to={linkTo}
        className="text-sm font-semibold flex items-center gap-1 hover:opacity-80 transition-opacity mt-1"
        style={{ color: highlight ? 'var(--accent)' : 'var(--ink-2)' }}>
        {link} <Icon name="chev" size={13} />
      </Link>
    </div>
  );
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded ${className}`} style={{ background: 'var(--surface-2)' }} />;
}

export function DashboardPage() {
  const { data: profile } = useProfile();
  const daysLeft = useTrialDaysLeft(profile);
  const qc = useQueryClient();

  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    const key = 'pp_onboarding_welcomed';
    if (!sessionStorage.getItem(key) && profile?.onboardingComplete) {
      if (sessionStorage.getItem('pp_just_onboarded')) {
        setShowWelcome(true);
        sessionStorage.removeItem('pp_just_onboarded');
        sessionStorage.setItem(key, '1');
        setTimeout(() => setShowWelcome(false), 5000);
      }
    }
  }, [profile]);

  const { data: plans = [], isLoading: plansLoading } = useQuery<MealPlanSummary[]>({
    queryKey: ['meal-plans'], queryFn: () => api.get('/api/meal-plans'), staleTime: 5 * 60_000,
  });
  const latestId = plans[0]?.id ?? null;
  const { data: latestPlanDetail, isLoading: detailLoading } = useQuery<MealPlanRow>({
    queryKey: ['meal-plan', latestId],
    queryFn: () => api.get(`/api/meal-plans/${latestId}`),
    staleTime: 5 * 60_000, enabled: !!latestId,
  });
const { data: denyList = [] } = useQuery<DenyListItem[]>({
    queryKey: ['deny-list'], queryFn: () => api.get('/api/deny-list'), staleTime: 5 * 60_000,
  });
  const { data: stats } = useQuery<UserStats>({
    queryKey: ['stats'], queryFn: () => api.get('/api/stats'), staleTime: 60_000,
  });
  const { data: challengeData } = useQuery<WeeklyChallenge>({
    queryKey: ['challenge-current'], queryFn: () => api.get('/api/challenges/current'), staleTime: 5 * 60_000,
  });
  const { data: favorites = [] } = useQuery<FavoriteRecipe[]>({
    queryKey: ['favorites'], queryFn: () => api.get('/api/favorites'), staleTime: 5 * 60_000,
  });
  const recentFavorites = useMemo(
    () => [...favorites].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 5),
    [favorites],
  );

  const todayStr = getTodayStr();
  const todayDay = latestPlanDetail?.planData?.days.find((d) => d.date === todayStr);

const nextMonday = getNextMonday();
  const nextWeekLabel = formatWeekRange(nextMonday);

  const [showPanic, setShowPanic] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [recipeMeal, setRecipeMeal] = useState<Meal | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [showGenFlow, setShowGenFlow] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Reopen the recipe modal after returning from cook mode.
  const reopenRecipeId = (location.state as { reopenRecipeId?: string } | null)?.reopenRecipeId;
  useEffect(() => {
    if (!reopenRecipeId || !latestPlanDetail?.planData?.days) return;
    for (const day of latestPlanDetail.planData.days) {
      const match = day.meals.find((m) => m.recipeId === reopenRecipeId);
      if (match) { setRecipeMeal(match as Meal); break; }
    }
    window.history.replaceState({}, '');
  }, [reopenRecipeId, latestPlanDetail?.planData?.days]);

  const generate = useMutation<{ planId: string; plan: MealPlanResponse }>({
    mutationFn: () => api.post('/api/meal-plans/generate', { weekStart: nextMonday }),
    onSuccess: () => {
      setGenerateError(null);
      qc.invalidateQueries({ queryKey: ['meal-plans'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      setShowConfetti(true);
    },
    onError: (err: any) => {
      setGenerateError(err?.body?.error ?? 'Failed to generate plan. Try again.');
    },
  });

  // ── Monday rating prompt ────────────────────────────────────────────
  const isMondayToday = new Date().getDay() === 1;
  const lastWeekPlan = plans.find((p) => p.weekStart < todayStr);
  const lastWeekPlanId = lastWeekPlan?.id ?? null;
  const showRatingPrompt = isMondayToday && !!lastWeekPlan;

  const { data: lastWeekDetail, isLoading: lastWeekLoading } = useQuery<MealPlanRow>({
    queryKey: ['meal-plan', lastWeekPlanId],
    queryFn: () => api.get(`/api/meal-plans/${lastWeekPlanId}`),
    staleTime: 5 * 60_000,
    enabled: showRatingPrompt && !!lastWeekPlanId,
  });

  const ratableMeals = useMemo(() => {
    if (!lastWeekDetail || !lastWeekPlanId) return [] as { recipeId: string; name: string; type: string; mealPlanId: string }[];
    return lastWeekDetail.planData.days.flatMap((d) =>
      d.meals
        .filter((m) => m.recipeId)
        .map((m) => ({
          recipeId: m.recipeId!,
          name: m.name,
          type: m.type,
          mealPlanId: lastWeekPlanId,
        })),
    );
  }, [lastWeekDetail, lastWeekPlanId]);

  const [mealRatings, setMealRatings] = useState<Record<string, number>>({});
  const [ratingSubmitted, setRatingSubmitted] = useState<boolean>(() => {
    const isoWeek = getISOWeek(new Date());
    return !!sessionStorage.getItem(`pp_rated_${isoWeek}`);
  });

  const submitRatings = useMutation({
    mutationFn: () => {
      const payload = Object.entries(mealRatings)
        .filter(([, rating]) => rating > 0)
        .map(([recipeId, rating]) => {
          const meal = ratableMeals.find((m) => m.recipeId === recipeId);
          return { recipeId, mealPlanId: meal?.mealPlanId ?? null, rating };
        });
      return api.post('/api/ratings', payload);
    },
    onSuccess: () => {
      const isoWeek = getISOWeek(new Date());
      if (!sessionStorage.getItem(`pp_rated_${isoWeek}`)) {
        sessionStorage.setItem(`pp_rated_${isoWeek}`, '1');
        setShowConfetti(true);
      }
      setRatingSubmitted(true);
      qc.invalidateQueries({ queryKey: ['stats'] });
      qc.invalidateQueries({ queryKey: ['achievements'] });
    },
  });

  const hasAnyRating = Object.values(mealRatings).some((r) => r > 0);
  const renderRatingPrompt = showRatingPrompt && (lastWeekLoading || ratableMeals.length > 0);

  const startGenerate = () => {
    setGenerateError(null);
    setShowGenFlow(true);
    generate.reset();
    generate.mutate();
  };

  const flowState: 'running' | 'success' | 'error' = generate.isPending
    ? 'running'
    : generate.isError
    ? 'error'
    : generate.isSuccess
    ? 'success'
    : 'running';

  const isLoading = plansLoading || detailLoading;
  const todayDate = new Date();
  const dateLabel = todayDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const firstName = profile?.name?.split(' ')[0] ?? 'friend';
  const greeting = greetingFor(todayDate.getHours());

  return (
    <Layout>
      <div className="w-full mx-auto" style={{ maxWidth: 1280, padding: '32px 24px 56px' }}>

        {/* Welcome flash */}
        {showWelcome && (
          <div className="mb-6 px-5 py-3 rounded-full text-sm font-semibold flex items-center gap-2"
            style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}>
            <Icon name="sparkle" size={14} /> Preferences saved. Generate your first plan below.
          </div>
        )}

        {/* Trial banner (>7 days) */}
        {profile?.tier === 'trial' && daysLeft !== null && daysLeft > 7 && (
          <div className="mb-6 rounded-2xl px-5 py-3 flex items-center justify-between gap-4"
            style={{
              background: 'color-mix(in oklab, var(--warning) 10%, var(--surface))',
              border: '1px solid color-mix(in oklab, var(--warning) 28%, var(--line-soft))',
            }}>
            <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
              Your free trial is active — <strong style={{ color: 'var(--ink)' }}>{daysLeft} days remaining</strong>.
            </p>
            <Link to="/profile" className="text-xs font-semibold whitespace-nowrap underline"
              style={{ color: 'var(--warning)' }}>
              Continue your subscription →
            </Link>
          </div>
        )}

        {/* ── Greeting + quick actions ──────────────────────────────────── */}
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'var(--ink-4)', letterSpacing: '0.1em' }}>
              {dateLabel}
            </div>
            <h1 className="h-display" style={{ fontSize: 'clamp(36px, 6vw, 56px)', margin: 0, color: 'var(--ink)', lineHeight: 1.0 }}>
              {greeting},<br />
              <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>{firstName}.</span>
            </h1>
          </div>
          <div className="flex gap-2.5 flex-wrap">
            <button className="btn btn-ghost" onClick={() => setShowPanic(true)} style={{ padding: '12px 18px', fontSize: 13.5 }}>
              <Icon name="bolt" size={15} /> Panic dinner
            </button>
            <button className="btn btn-primary"
              onClick={startGenerate}
              disabled={generate.isPending}
              style={{ padding: '12px 22px', fontSize: 14, opacity: generate.isPending ? 0.6 : 1 }}>
              <Icon name="sparkle" size={15} />
              Generate next week
            </button>
          </div>
        </div>

        {/* ── Today ─────────────────────────────────────────────────────── */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider mb-1.5"
                style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>Today</div>
              <h2 className="h-serif" style={{ fontSize: 26, margin: 0, color: 'var(--ink)' }}>What you're eating</h2>
            </div>
            {latestId && (
              <Link to={`/meal-plans/${latestId}`}
                className="text-sm font-medium flex items-center gap-1 hover:opacity-80"
                style={{ color: 'var(--ink-3)' }}>
                View full week <Icon name="chev" size={14} />
              </Link>
            )}
          </div>

          {isLoading ? (
            <div className="grid sm:grid-cols-3 gap-4">
              {[0, 1, 2].map(i => <Skeleton key={i} className="h-44" />)}
            </div>
          ) : !latestId ? (
            <div className="card p-8 text-center">
              <p className="text-sm mb-4" style={{ color: 'var(--ink-3)' }}>
                No plan yet. Build your first one in under a minute.
              </p>
              <button className="btn btn-primary" onClick={startGenerate} disabled={generate.isPending}>
                <Icon name="sparkle" size={14} />
                Generate your first plan
              </button>
            </div>
          ) : !todayDay ? (
            <div className="card p-6 text-sm" style={{ color: 'var(--ink-3)' }}>
              Today isn't in your current plan. <Link to={`/meal-plans/${latestId}`} className="hover:underline" style={{ color: 'var(--accent)' }}>View plan →</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-4">
              {todayDay.meals.map((meal) => (
                <button key={meal.type}
                  onClick={() => setRecipeMeal(meal as Meal)}
                  className="card flex flex-col text-left transition-transform hover:-translate-y-0.5"
                  style={{ padding: 0, overflow: 'hidden', cursor: 'pointer', fontFamily: 'inherit', color: 'inherit' }}>
                  <Placeholder tone={MEAL_TONE[meal.type] ?? 'warm'} label={meal.name.toLowerCase()} style={{ height: 110 }} />
                  <div className="px-4 py-4">
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--ink-4)', letterSpacing: '0.14em' }}>
                        {meal.type}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--ink-4)' }}>
                        {meal.ingredients.length} ingredient{meal.ingredients.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="h-serif" style={{ fontSize: 16, color: 'var(--ink)', lineHeight: 1.25 }}>{meal.name}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* ── Week + Generate next week ─────────────────────────────────── */}
        <section className="grid lg:grid-cols-[1.6fr_1fr] gap-5 mb-8">
          {/* This week */}
          <div className="card p-6">
            <div className="flex justify-between items-center mb-5 gap-3 flex-wrap">
              <div>
                <h3 className="h-serif" style={{ fontSize: 22, margin: 0, color: 'var(--ink)' }}>This week's plan</h3>
                <div className="text-xs mt-1" style={{ color: 'var(--ink-4)' }}>
                  {plans[0] ? formatWeekRange(plans[0].weekStart) : '—'} ·{' '}
                  {latestPlanDetail?.planData?.days.length ?? 0} days
                </div>
              </div>
              {latestId && (
                <Link to={`/meal-plans/${latestId}`} className="btn btn-soft" style={{ padding: '8px 14px', fontSize: 12.5 }}>
                  Open week →
                </Link>
              )}
            </div>

            {plansLoading ? (
              <Skeleton className="h-32" />
            ) : !plans[0] || !latestPlanDetail ? (
              <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
                Generate a plan to see your week at a glance.
              </p>
            ) : (
              <div className="grid grid-cols-7 gap-2">
                {latestPlanDetail.planData.days.map((d) => {
                  const isToday = d.date === todayStr;
                  const dayName = new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' });
                  const dayNum = new Date(d.date + 'T00:00:00').getDate();
                  return (
                    <div key={d.date}
                      className="rounded-xl p-3 flex flex-col gap-1.5 min-h-[120px]"
                      style={{
                        background: isToday ? 'var(--accent-soft)' : 'var(--surface-2)',
                        border: `1px solid ${isToday ? 'var(--accent)' : 'var(--line-soft)'}`,
                      }}>
                      <div className="text-[11px] font-semibold uppercase"
                        style={{ color: isToday ? 'var(--accent)' : 'var(--ink-4)', letterSpacing: '0.06em' }}>
                        {dayName}
                      </div>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 500, lineHeight: 1, color: isToday ? 'var(--accent)' : 'var(--ink-2)' }}>
                        {dayNum}
                      </div>
                      <div className="flex flex-col gap-1 mt-auto">
                        {d.meals.slice(0, 3).map((_, i) => (
                          <div key={i} className="h-1 rounded"
                            style={{ background: isToday ? 'var(--accent)' : 'var(--accent-soft)', opacity: 0.5 + i * 0.15 }} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Generate next week */}
          <div className="card p-6 flex flex-col gap-4 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-48 h-48 pointer-events-none"
              style={{ background: 'radial-gradient(circle, var(--accent-glow) 0%, transparent 70%)' }} />
            <div className="relative">
              <div className="text-xs font-semibold uppercase mb-2.5"
                style={{ color: 'var(--ink-4)', letterSpacing: '0.14em' }}>Next week</div>
              <div className="h-serif" style={{ fontSize: 22, color: 'var(--ink)', marginBottom: 6 }}>{nextWeekLabel}</div>
              <div className="text-sm" style={{ color: 'var(--ink-3)' }}>Drops Sunday at 6am — or build it now.</div>
            </div>
            <div className="mt-auto flex flex-col gap-2 relative">
              <button className="btn btn-primary"
                onClick={startGenerate}
                disabled={generate.isPending}
                style={{ padding: '14px 18px', fontSize: 14, justifyContent: 'space-between', width: '100%', opacity: generate.isPending ? 0.6 : 1 }}>
                <span className="flex items-center gap-2">
                  <Icon name="sparkle" size={15} />
                  Generate now
                </span>
                <Icon name="arrow" size={14} />
              </button>
              <Link to="/meal-plans" className="btn btn-ghost text-center" style={{ padding: '12px 18px', fontSize: 13, width: '100%' }}>
                Build manually
              </Link>
            </div>
            {generateError && <p className="text-xs" style={{ color: 'var(--danger)' }}>{generateError}</p>}
          </div>
        </section>

        {/* ── stat row ─────────────────────────────────────────────────── */}
        <section className="grid sm:grid-cols-2 gap-5 mb-8">
          <StatCard icon="ban" label="Avoid list" value={denyList.length}
            sub={`ingredient${denyList.length === 1 ? '' : 's'} flagged`}
            link="Edit list" linkTo="/deny-list"
          />
          <StatCard icon="plate" label="Plans built" value={stats?.totalPlans ?? plans.length}
            sub="all-time" link="View history" linkTo="/meal-plans" highlight
          />
        </section>

        {/* ── Food waste saved widget ──────────────────────────────────── */}
        <section className="mb-8">
          <WasteSavingsWidget />
        </section>

        {/* ── Gamification: XP + Challenge + Savings (when present) ─────── */}
        {(stats || challengeData?.challenge) && (
          <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {stats && (
              <div className="card p-6">
                <div className="text-xs font-semibold uppercase tracking-wider mb-3"
                  style={{ color: 'var(--ink-3)', letterSpacing: '0.08em' }}>
                  Chef progress
                </div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs" style={{ color: 'var(--ink-4)' }}>Lv {stats.level} · {stats.levelTitle}</p>
                    <p className="h-display" style={{ fontSize: 28, color: 'var(--accent)', lineHeight: 1, marginTop: 2 }}>
                      {stats.totalXp.toLocaleString()} XP
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="h-display" style={{ fontSize: 28, color: 'var(--ink)', lineHeight: 1 }}>
                      {stats.weekStreak}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--ink-4)' }}>week streak</p>
                  </div>
                </div>
                {stats.nextLevelXp && (
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                    <div className="h-full rounded-full" style={{
                      width: `${Math.min(100, ((stats.totalXp - (LEVEL_XP[stats.level - 1] ?? 0)) / (stats.nextLevelXp - (LEVEL_XP[stats.level - 1] ?? 0))) * 100)}%`,
                      background: 'var(--accent)',
                    }} />
                  </div>
                )}
                <Link to="/achievements" className="text-sm font-semibold mt-3 inline-flex items-center gap-1 hover:opacity-80"
                  style={{ color: 'var(--accent)' }}>
                  Achievements <Icon name="chev" size={13} />
                </Link>
              </div>
            )}
            {challengeData?.challenge && (
              <div className="card p-6">
                <div className="text-xs font-semibold uppercase tracking-wider mb-2"
                  style={{ color: 'var(--ink-3)', letterSpacing: '0.08em' }}>Weekly challenge</div>
                <div className="h-serif" style={{ fontSize: 18, color: 'var(--ink)', marginBottom: 6 }}>
                  {challengeData.challenge.title}
                </div>
                <p className="text-sm mb-3" style={{ color: 'var(--ink-3)', lineHeight: 1.5 }}>
                  {challengeData.challenge.description}
                </p>
                {challengeData.progress?.completedAt ? (
                  <span className="chip" data-on="1" style={{ fontSize: 11.5 }}>
                    <Icon name="check" size={11} stroke={2.4} /> +{challengeData.challenge.xpReward} XP
                  </span>
                ) : (
                  <span className="text-xs" style={{ color: 'var(--ink-4)' }}>
                    +{challengeData.challenge.xpReward} XP on completion
                  </span>
                )}
              </div>
            )}
            {stats && stats.totalPlans > 0 && profile && (
              <div className="card p-6">
                <div className="text-xs font-semibold uppercase tracking-wider mb-2"
                  style={{ color: 'var(--ink-3)', letterSpacing: '0.08em' }}>Estimated savings</div>
                <p className="h-display" style={{ fontSize: 36, color: 'var(--accent)', lineHeight: 1, marginBottom: 8 }}>
                  ${(stats.totalPlans * (profile.familySize ?? 2) * (RESTAURANT_PPP - HOME_PPP) * 7).toLocaleString()}
                </p>
                <p className="text-sm" style={{ color: 'var(--ink-3)' }}>vs. eating out every meal</p>
              </div>
            )}
          </section>
        )}

        {/* ── Monday rating prompt ──────────────────────────────────────── */}
        {renderRatingPrompt && (
          <section className="mb-8">
            <div className="card p-6">
              <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider mb-1.5"
                    style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
                    How was last week?
                  </div>
                  <h2 className="h-serif" style={{ fontSize: 22, margin: 0, color: 'var(--ink)' }}>
                    {lastWeekPlan ? formatWeekRange(lastWeekPlan.weekStart) : ''}
                  </h2>
                </div>
                {!ratingSubmitted && hasAnyRating && (
                  <span className="text-xs" style={{ color: 'var(--ink-4)' }}>
                    {Object.values(mealRatings).filter((r) => r > 0).length} rated
                  </span>
                )}
              </div>

              {ratingSubmitted ? (
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink-3)' }}>
                  <Icon name="check" size={14} stroke={2.4} />
                  Ratings saved. Thanks for the feedback!
                </div>
              ) : lastWeekLoading ? (
                <div className="flex flex-col gap-2">
                  {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}
                </div>
              ) : (
                <>
                  <div className="flex flex-col divide-y overflow-y-auto"
                    style={{ borderColor: 'var(--line-soft)', maxHeight: 360 }}>
                    {ratableMeals.map((meal) => {
                      const value = mealRatings[meal.recipeId] ?? 0;
                      return (
                        <div key={`${meal.mealPlanId}-${meal.recipeId}`}
                          className="flex items-center justify-between gap-3 py-2"
                          style={{ minHeight: 44 }}>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-[11px] font-semibold uppercase tracking-wider"
                              style={{ color: 'var(--ink-4)', letterSpacing: '0.14em' }}>
                              {meal.type}
                            </span>
                            <span className="text-sm line-clamp-1" style={{ color: 'var(--ink)' }}>
                              {meal.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            {[1, 2, 3, 4, 5].map((i) => {
                              const filled = value >= i;
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  aria-label={`Rate ${meal.name} ${i} star${i === 1 ? '' : 's'}`}
                                  onClick={() =>
                                    setMealRatings((prev) => ({ ...prev, [meal.recipeId]: i }))
                                  }
                                  className="leading-none"
                                  style={{
                                    background: 'transparent',
                                    border: 0,
                                    cursor: 'pointer',
                                    padding: '8px 4px',
                                    fontSize: 20,
                                    color: filled ? 'var(--accent)' : 'var(--ink-4)',
                                  }}>
                                  {filled ? '★' : '☆'}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-end mt-4 gap-3">
                    {submitRatings.isError && (
                      <span className="text-xs" style={{ color: 'var(--danger, #b91c1c)' }}>
                        Couldn't save. Try again.
                      </span>
                    )}
                    <button
                      className="btn btn-primary"
                      onClick={() => submitRatings.mutate()}
                      disabled={!hasAnyRating || submitRatings.isPending}
                      style={{ padding: '10px 18px', fontSize: 13.5, opacity: !hasAnyRating || submitRatings.isPending ? 0.55 : 1 }}>
                      {submitRatings.isPending ? 'Saving…' : 'Submit ratings'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* ── Favorites widget (hidden when user has none) ──────────────── */}
        {favorites.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider mb-1.5"
                  style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
                  <Icon name="heart" size={12} style={{ display: 'inline', marginRight: 6, marginBottom: -1 }} />
                  Favorites · {favorites.length}
                </div>
                <h2 className="h-serif" style={{ fontSize: 26, margin: 0, color: 'var(--ink)' }}>Saved recipes</h2>
              </div>
              <Link to="/favorites"
                className="text-sm font-medium flex items-center gap-1 hover:opacity-80"
                style={{ color: 'var(--ink-3)' }}>
                View all <Icon name="chev" size={14} />
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollSnapType: 'x mandatory' }}>
              {recentFavorites.map((fav) => {
                const accentClass = FAV_ACCENT[fav.mealType] ?? '';
                return (
                  <Link key={fav.id} to={`/recipes/${fav.recipeId}`}
                    className={`card ${accentClass} flex-shrink-0 flex flex-col transition-transform hover:-translate-y-0.5`}
                    style={{
                      width: 200, padding: 0, overflow: 'hidden',
                      scrollSnapAlign: 'start', textDecoration: 'none',
                    }}>
                    <Placeholder tone={MEAL_TONE[fav.mealType] ?? 'warm'} label={fav.recipeName.toLowerCase()} style={{ height: 90 }} />
                    <div className="px-4 py-3.5">
                      <div className="text-[11px] font-semibold uppercase tracking-wider mb-1"
                        style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
                        {fav.mealType}
                      </div>
                      <div className="h-serif" style={{
                        fontSize: 14.5, color: 'var(--ink)', lineHeight: 1.25,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {fav.recipeName}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Activity ──────────────────────────────────────────────────── */}
        <section className="mb-8">
          <div className="card p-6">
            <h3 className="h-serif" style={{ fontSize: 20, margin: 0, color: 'var(--ink)', marginBottom: 12 }}>Activity</h3>
            {plansLoading ? (
              <Skeleton className="h-12" />
            ) : plans.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
                No meal plans yet — generate your first one above to get started.
              </p>
            ) : (
              <>
                <p className="text-sm mb-3" style={{ color: 'var(--ink-2)' }}>
                  You've generated <strong style={{ color: 'var(--ink)' }}>{plans.length}</strong> meal plan{plans.length === 1 ? '' : 's'} so far.
                </p>
                <div className="flex flex-wrap gap-2">
                  {plans.slice(0, 4).map((plan) => (
                    <Link key={plan.id} to={`/meal-plans/${plan.id}`}
                      className="chip"
                      style={{ fontSize: 12 }}>
                      Week of {new Date(plan.weekStart + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </Link>
                  ))}
                  {plans.length > 4 && (
                    <Link to="/meal-plans" className="chip" style={{ fontSize: 12, color: 'var(--ink-4)' }}>
                      +{plans.length - 4} more
                    </Link>
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        {/* ── Getting started checklist (trial users with no plans) ─────── */}
        {profile?.tier === 'trial' && !plansLoading && plans.length === 0 && (
          <section className="card p-6 mb-8">
            <h3 className="h-serif" style={{ fontSize: 20, margin: 0, color: 'var(--ink)', marginBottom: 4 }}>Getting started</h3>
            <p className="text-sm mb-5" style={{ color: 'var(--ink-3)' }}>Three steps. Five minutes total.</p>
            <ol className="flex flex-col gap-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}>
                  <Icon name="check" size={13} stroke={2.4} />
                </span>
                <div>
                  <p style={{ color: 'var(--ink)', fontWeight: 500 }}>Account created</p>
                  <p className="text-xs" style={{ color: 'var(--ink-4)' }}>Your 30-day trial is active.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)', color: 'var(--ink-3)' }}>
                  2
                </span>
                <div>
                  <Link to="/onboarding" className="font-semibold hover:underline" style={{ color: 'var(--accent)' }}>
                    Set your preferences →
                  </Link>
                  <p className="text-xs" style={{ color: 'var(--ink-4)' }}>Diet, cuisine, family size.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)', color: 'var(--ink-3)' }}>
                  3
                </span>
                <div>
                  <Link to="/meal-plans" className="font-semibold hover:underline" style={{ color: 'var(--accent)' }}>
                    Generate your first plan →
                  </Link>
                  <p className="text-xs" style={{ color: 'var(--ink-4)' }}>21 meals tailored to your family.</p>
                </div>
              </li>
            </ol>
          </section>
        )}

      </div>

      {showConfetti && <Confetti onDone={() => setShowConfetti(false)} />}
      {showPanic && <PanicModal onClose={() => setShowPanic(false)} />}
      {recipeMeal && latestId && (
        <RecipeModal meal={recipeMeal} onClose={() => setRecipeMeal(null)} />
      )}
      {showGenFlow && (
        <GeneratePlanFlow
          state={flowState}
          errorMessage={generateError}
          blwActive={profile?.familyMembers?.some((m) => m.age === 0) ?? false}
          hiddenVeggieMode={profile?.hiddenVeggieMode ?? false}
          pickyEaterMode={profile?.pickyEaterMode ?? false}
          onReview={() => {
            const id = generate.data?.planId;
            setShowGenFlow(false);
            if (id) navigate(`/meal-plans/${id}`);
            else navigate('/meal-plans');
          }}
          onReroll={() => {
            generate.reset();
            generate.mutate();
          }}
          onClose={() => {
            setShowGenFlow(false);
            generate.reset();
          }}
        />
      )}
    </Layout>
  );
}
